import { selector } from '@/lib/selectors';
// eslint-disable-next-line no-restricted-syntax
import { loadedDataCompanyIdSelector } from '@/domain/documents/documentsLeafSelectors';
import { currentCompanyIdSelector } from '@/domain/router/routerLeafSelectors';

import { documentsWithUniqLinked, sorterByFavoriteOrCreated } from '@/domain/documents/helpers';
import {
  isDokkaSupportSelector,
  userGUIDSelector,
  userIdSelector,
  payModuleCompaniesContextSelector,
} from '@/domain/env/envSelector';
import {
  chatParticipantsSelector,
  chatParticipantsByIdSelector,
  chatUsersCompanyIdSelector,
} from '@/domain/chat/chatSelector';
import CONST from '@/domain/documents/constants';

import { categoriesListSelector } from '@/domain/categories/categoriesLeafSelectors';

import {
  companiesByIdSelector,
  companySigningsCompanyIdSelector,
  configurationCompanySelector,
} from '@/domain/companies/companiesSelector';
import { companyFeatureFlagsSelector } from '@/domain/companies/companiesLeafSelectors';

import { CachedDocumentFactory } from '@/domain/documents/factories';
import { textractEnabledSelector } from '@/domain/textract/selectors';
import { textractForVendorEnabledSelector } from '@/domain/journal/selectors';

const documents = (state) => state.documents;

export const editableDocSelector = selector(documents, (d) => d.editableDoc);
export const pageTokenSelector = selector(documents, (d) => d.pageToken);
export const documentsByIdSelector = selector(documents, (d) => d.list);

export const aggregationsSelector = selector(documents, (d) => d.aggregations);
export const subtypeFilterSelector = selector(documents, (d) => d.subtypeFilter);
export const linkedSelector = selector(documents, (d) => d.linked);
export const linkingTagSelector = selector(documents, (d) => d.linkingTag);
export const documentLinkedSelector = selector(linkedSelector, (l) => l.list);
export const documentLinkedOpenStatusSelector = selector(linkedSelector, (l) => l.isOpen && !!l.list.size);
export const documentsCurrentCountSelector = selector(documents, (d) => d.company_total);
export const processingDocsCountSelector = selector(documents, (d) => d.processing);
export const getDocumentsForBulkApprove = (documentIds) =>
  selector(documentsByIdSelector, userGUIDSelector, (documentList, uID) =>
    documentList.filter((d) => {
      const next = d.getIn(['approvals', 'next']);
      const currentSigners = Array.isArray(next) ? next : [next];
      const approvalStatus = d.getIn(['approvals', 'status']);
      const isFlowActive = approvalStatus !== 'draft' && approvalStatus !== null;
      const isForbiddenTags = [CONST.tags.isScheduledApproval, CONST.tags.isScheduledAcceptance].some((tag) =>
        d.tags.has(tag),
      );

      return documentIds.includes(d.documentID) && !isForbiddenTags && currentSigners.includes(uID) && isFlowActive;
    }),
  );
export const getDocumentsForBulkPaid = (documentIds) =>
  selector(documentsByIdSelector, (documentList) =>
    documentList.filter((d) => {
      // if b-end dont detect paid status of document, by default we mark as UNPAID, and we can change to PAID
      const isUnknownType = ![CONST.tags.unpaid, CONST.tags.paid].some((t) => d.tags.has(t));

      return (
        documentIds.includes(d.documentID) &&
        d.tags.has(CONST.tags.financial) &&
        (d.tags.has(CONST.tags.unpaid) || isUnknownType)
      );
    }),
  );
export const getDocumentsForBulkUnpaid = (documentIds) =>
  selector(documentsByIdSelector, (documentList) =>
    documentList.filter(
      (d) => documentIds.includes(d.documentID) && d.tags.has(CONST.tags.financial) && d.tags.has(CONST.tags.paid),
    ),
  );
export const getDocumentsForBulkPermanentlyDeleteByCreator = (documentIds) =>
  selector(documentsByIdSelector, userIdSelector, (documentList, userId) =>
    documentList.filter((d) => {
      return documentIds.includes(d.documentID) && d.tags.has(CONST.tags.trash) && d.creator === userId;
    }),
  );
export const getDocumentsForBulkActivateApprovalFlow = (documentIds) =>
  selector(documentsByIdSelector, (documentList) =>
    documentList.filter((d) => documentIds.includes(d.documentID) && d.approvals?.status === 'draft'),
  );

export const getDocumentsForBulkDeactivateApprovalFlow = (documentIds) =>
  selector(documentsByIdSelector, (documentList) =>
    documentList.filter((d) => documentIds.includes(d.documentID) && d.approvals?.status === 'active'),
  );

export const getDocumentsForBulkSendToPay = (documentIds) =>
  selector(
    documentsByIdSelector,
    currentCompanyIdSelector,
    payModuleCompaniesContextSelector,
    companyFeatureFlagsSelector,
    (documentList, companyId, payModuleCompanies, companyFeatureFlags) => {
      // Non-published (not yet accepted) invoices are eligible only when the company
      // enables `enable_prepare_payment_for_non_published_invoices`.
      const allowNonPublished = companyFeatureFlags.get('enable_prepare_payment_for_non_published_invoices', false);

      return documentList.filter(
        (d) =>
          documentIds.includes(d.documentID) &&
          companyId &&
          payModuleCompanies.includes(companyId) &&
          d.doctype === 'financial' &&
          (allowNonPublished || d.tags.has(CONST.tags.isAccepted)) &&
          !d.tags.has(CONST.tags.isBeingProcessed) &&
          !d.tags.has(CONST.tags.paymentInProgress),
      );
    },
  );

export const getDocumentsForBulkApproverReplace = (documentIds, user) =>
  selector(documentsByIdSelector, (documentList) =>
    documentList
      .filter((d) => {
        if (documentIds.includes(d.documentID) && d.approvals?.nodes && !d.tags.has(CONST.tags.approvalComplete)) {
          const a = d.approvals.nodes.filter(
            ({ status, username }) => username === user && (status === 'pending' || status === 'draft'),
          );
          return !!a.length;
        }
        return false;
      })
      .keySeq()
      .toArray(),
  );
// check document ids if they are mixed (single document and linked are together)
export const isMixedDocumentsForBulkMoveToCompanySelector = (documentIds) =>
  selector(documentsByIdSelector, (documentList) =>
    Boolean(
      documentList
        .filter((d) => documentIds.includes(d.documentID) && d.linkid)
        .keySeq()
        .toArray().length,
    ),
  );

export const getGridDocumentsByPageIndexSelector = selector(documents, (d) => d.gridDocumentsByPageIndex);
export const getGridSearchAfterByPageIndexSelector = selector(documents, (d) => d.gridSearchAfterByPageIndex);

export { loadedDataCompanyIdSelector };
// documents List that need to be updated - after bulk actions
export const gridApplyTransactionSelector = selector(documents, (d) => d.gridApplyTransaction);

export const justPublishedDocumentSelector = selector(documents, (d) => d.justPublished);
export const isPublishNavigationPendingSelector = selector(documents, (d) => d.isPublishNavigationPending);
export const loadedSelector = selector(documents, (d) => d.loaded);
export const documentSortedLinkedSelector = selector(documentLinkedSelector, (d) =>
  d.toList().sort(sorterByFavoriteOrCreated),
);
export const isLinkedDocsLockedSelector = selector(documentLinkedSelector, (docs) =>
  docs.some((doc) => doc.linkid.endsWith('locked')),
);
export const documentLinkedTagSelector = selector(linkedSelector, (d) => d.tag);
export const documentSelector = selector(documents, (d) => d.document);
export const documentStatusesSelector = selector(documents, (d) => d.document.status);
export const documentReasonSelector = selector(documents, (d) => d.document.reason);
export const documentLastModifiedDateSelector = selector(documents, (d) => d.document.lastModifiedDate);
export const documentDocumentIdSelector = selector(documents, (d) => d.document.documentID);
export const documentDoctypeSelector = selector(documents, (d) => d.document.doctype);
export const documentTagsSelector = selector(documents, (d) => d.document.tags);
export const documentCountSelector = selector(documents, (d) => d.count);
export const documentsListListSelector = selector(documentsByIdSelector, (d) => d.toList());
export const documentsSelector = selector(documentsListListSelector, (d) => documentsWithUniqLinked(d));
export const uploadQueueSelector = selector(documents, (d) => d.uploadQueue);

export const erpDuplicatedSelector = selector(documents, (d) => d.erpDuplicated);
export const isDocumentConfidentialSelector = selector(documentSelector, (d) => d.tags.has('_S_CONFIDENTIAL'));
export const isDocumentPaidSelector = selector(documentSelector, (d) => d.tags.has(CONST.tags.paid));
export const isDocumentScheduledApprovalSelector = selector(documentSelector, (d) =>
  d.tags.has(CONST.tags.isScheduledApproval),
);

export const isTwoWayMatchOnSelector = selector(
  textractEnabledSelector,
  documentSelector,
  textractForVendorEnabledSelector,
  (textractEnabled, d, textractForVendorEnabled) =>
    textractEnabled &&
    textractForVendorEnabled &&
    !d.tags.has(CONST.tags.isAccepted) &&
    !d.tags.has(CONST.tags.isBeingProcessed),
);

export const totalNumberOfDocumentsSelector = selector(documents, (d) => d.count_without_link_panels);

export const isPendingUploadSelector = selector(uploadQueueSelector, (d) => d.some((it) => it.status === 'pending'));

export const documentsGridHeadersListSelector = selector(documents, (d) => d.gridHeadersList);

export const gridHasNewDocumentsSelector = selector(
  documents,
  (d) =>
    Boolean(d.fetchedDocumentsTimeStamp) &&
    Boolean(d.latestWorkerTimeStamp) &&
    d.fetchedDocumentsTimeStamp !== d.latestWorkerTimeStamp,
);

export const documentsGridLoaderSelector = selector(documents, (d) => d.gridLoader);
export const documentHotkeysSelector = selector(documents, (d) => d.hotkeys.data);
export const documentHotkeysCompanyIdSelector = selector(documents, (d) => d.hotkeys.companyId);
export const documentHotkeysIsLoadingSelector = selector(documents, (d) => d.hotkeys.isLoading);

export const cachedDocumentSelector = (docHash) =>
  selector(documents, (d) => d.cachedDocuments.find(({ hash }) => hash === docHash) || CachedDocumentFactory());

// Entering a company signs in and reloads only when the store holds another company's data.
export const isStoreOnAnotherCompany = (companyId) =>
  selector(loadedDataCompanyIdSelector, (loadedDataCompanyId) => companyId !== loadedDataCompanyId);

export const shouldProcessingDocBeBlockedSelector = selector(
  documentSelector,
  isDokkaSupportSelector,
  (d, isSupport) => d.tags.has(CONST.tags.isBeingProcessed) && !isSupport,
);

export const shouldRenewCategories = (companyId) =>
  selector(
    categoriesListSelector,
    isStoreOnAnotherCompany(companyId),
    (list, isAnotherCompany) => !list.size || isAnotherCompany,
  );

export const shouldRenewConfigurationCompany = (companyId) =>
  selector(
    configurationCompanySelector,
    (configurationCompany) => !configurationCompany.get('id') || configurationCompany.get('id') !== companyId,
  );

export const shouldRenewCompanySignings = (companyId) =>
  selector(companySigningsCompanyIdSelector, (signingsCompanyId) => signingsCompanyId !== companyId);

export const shouldRenewCompanyHotkeys = (companyId) =>
  selector(documentHotkeysCompanyIdSelector, (hotkeysCompanyId) => hotkeysCompanyId !== companyId);

// work only if the user went to the document page via a direct link
export const shouldRenewCompanyPreferences = (companyId) =>
  selector(
    companiesByIdSelector,
    documentsByIdSelector,
    (companies, docs) => !companies.getIn([companyId, 'signParams', 'guid']) && !docs.size,
  );

export const shouldRenewCompanyPreferencesOnWorkspace = (companyId) =>
  selector(companiesByIdSelector, (companies) => !companies.getIn([companyId, 'signParams', 'guid']));

export const shouldInvalidateDocumentCacheSelector = selector(documents, (d) => d.shouldInvalidateDocumentCache);

export const shouldRenewCompanyChatPears = (companyId) =>
  selector(
    chatParticipantsSelector,
    chatUsersCompanyIdSelector,
    (users, usersCompanyId) => !users.size || usersCompanyId !== companyId,
  );

export const currentViewersSelector = selector(
  documents,
  chatParticipantsByIdSelector,
  userIdSelector,
  // exclude current user and ghost, as ghost is not presented in chat peers
  // no other way to tell if another user is ghost except for its absence in chat peers
  (d, users, currentUserId) => d.currentViewers.filter((userId) => userId !== currentUserId && users.has(userId)),
);
